import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import RelatoriosAtividadesFilter, { CreateNewOptions } from '@/modules/reports/presentation/components/RelatoriosAtividadesFilter';
import RelatoriosAtividadesHeader from '@/modules/reports/presentation/components/RelatoriosAtividadesHeader';
import RelatoriosAtividadesList from '@/modules/reports/presentation/components/RelatoriosAtividadesList';
import RelatoriosAtividadesNotificacoes from '@/modules/reports/presentation/components/RelatoriosAtividadesNotificacoes';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
import { BuscarDefensorPorCPF, BuscarRelatoriosDoDefensor, BuscarTodasLotacoesDefensor, DefensoriaOpcao, DeletarRelatorio, ExtrairDefensoriasOpcoes, ExtrairDefensoriasOpcoesPorMes, LotacaoTitular, ObterComarcaVara, ObterLotacaoTitular, ObterTitularidade, RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import { usuarioTemSuporteAdministrativoRelatorios } from '@/modules/reports/application/relatorios-acesso.utils';
import { getTokenPayload } from '@/modules/reports/application/relatorios-atividades.utils';
import { CarregarPermissoes } from '@/modules/system/auth/application/auth.service';
import { Container, Modal, Button as BootstrapButton, Alert } from 'react-bootstrap';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { ReadDefensorResponseDto, ReadLotacaoDto } from '@/api.types';
import { toast } from 'react-toastify';
import { Link, useLocation, useNavigate } from 'react-router-dom';
export default function RelatoriosAtividadesPage() {
  const context = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = useRef(false);
  const now = new Date();
  const mesAtualPad = String(now.getMonth() + 1).padStart(2, '0');
  const anoAtual = now.getFullYear();

  if (!context) {
    throw new Error('RelatoriosAtividadesPage deve ser usado dentro de um MyProvider');
  }

  const { state, authVisao } = context;
  const { permissions } = useNavigationView();

  // Estados
  const [loading, setLoading] = useState(true);
  const [defensorData, setDefensorData] = useState<ReadDefensorResponseDto | null>(null);
  const [lotacoes, setLotacoes] = useState<ReadLotacaoDto[]>([]);
  const [defensoriasOpcoes, setDefensoriasOpcoes] = useState<DefensoriaOpcao[]>([]);
  const [, setMesSelecionado] = useState<string>(mesAtualPad);
  const [, setAnoSelecionado] = useState<number | null>(anoAtual);
  const [comarca, setComarca] = useState('');
  const [vara, setVara] = useState('');
  const [titularidade, setTitularidade] = useState('nao-definido');
  const [lotacaoTitular, setLotacaoTitular] = useState<LotacaoTitular | null>(null);
  const [relatorios, setRelatorios] = useState<RelatorioAtividadeDto[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [relatorioParaDeletar, setRelatorioParaDeletar] = useState<RelatorioAtividadeDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [modoSuporteAdministrativo, setModoSuporteAdministrativo] = useState(false);

  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const podeSuporteAdministrativoRelatorios = useMemo(
    () => usuarioTemSuporteAdministrativoRelatorios(authVisao, permissoes),
    [authVisao, permissoes],
  );

  const redirectOnce = (to: string) => {
    if (redirectedRef.current || location.pathname === to) {
      return;
    }
    redirectedRef.current = true;
    navigate(to, { replace: true });
  };

  const handlePeriodoChange = (mes: string, ano: number) => {
    setMesSelecionado(mes);
    setAnoSelecionado(ano);
    if (lotacoes && lotacoes.length > 0 && mes && ano) {
      const opcoesPeriodo = ExtrairDefensoriasOpcoesPorMes(lotacoes, mes, ano);
      setDefensoriasOpcoes(opcoesPeriodo);
    } else if (lotacoes && lotacoes.length > 0) {
      // Fallback: se limpar mês/ano, volta para todas as defensorias possíveis
      const opcoes = ExtrairDefensoriasOpcoes(lotacoes);
      setDefensoriasOpcoes(opcoes);
    }
  };

  // Carregar dados: defensor → fluxo atual; colaborador corregedoria → redireciona; ADMIN_ACTIONS → painel de suporte; outro → acesso restrito
  useEffect(() => {
    const cpfToUse = state.cpf || getTokenPayload()?.personCPF;

    const carregarDados = async () => {
      try {
        setLoading(true);
        setAccessDenied(false);
        setModoSuporteAdministrativo(false);

        if (!cpfToUse) {
          if (podeSuporteAdministrativoRelatorios) {
            setModoSuporteAdministrativo(true);
            setLoading(false);
            return;
          }

          // Login sem CPF (ex.: usuário colaborador): redireciona só com visão Corregedoria
          if (authVisao === NavigationView.Corregedoria) {
            redirectOnce(Links.Relatorios_Corregedoria);
            return;
          }
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Buscar defensor por CPF
        const defensor = await BuscarDefensorPorCPF(cpfToUse);
        if (!defensor) {
          if (podeSuporteAdministrativoRelatorios) {
            setModoSuporteAdministrativo(true);
            setLoading(false);
            return;
          }

          // Não é defensor: redireciona só com visão Corregedoria
          if (authVisao === NavigationView.Corregedoria) {
            redirectOnce(Links.Relatorios_Corregedoria);
            return;
          }
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        setDefensorData(defensor);

        // Buscar todas as lotações do defensor
        const lotacoesData = await BuscarTodasLotacoesDefensor(defensor.id);
        setLotacoes(lotacoesData);

        // Carregar defensorias já filtradas pelo período inicial (mês/ano atual),
        // para evitar variação visual ao abrir "Criar Novo Relatório".
        const opcoes = ExtrairDefensoriasOpcoesPorMes(lotacoesData, mesAtualPad, anoAtual);
        setDefensoriasOpcoes(opcoes);

        // Obter comarca e vara
        const { comarca: comarcaData, vara: varaData } = ObterComarcaVara(defensor, lotacoesData);
        setComarca(comarcaData);
        setVara(varaData);

        // Obter titularidade (passa também as lotações para análise)
        const titularidadeData = ObterTitularidade(defensor, lotacoesData);
        setTitularidade(titularidadeData);

        // Obter lotação titular
        const lotacaoTitularData = ObterLotacaoTitular(lotacoesData);
        setLotacaoTitular(lotacaoTitularData);

        // Buscar relatórios iniciais (sem filtro)
        const relatoriosData = await BuscarRelatoriosDoDefensor(defensor.id);
        setRelatorios(relatoriosData);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast.error('Erro ao carregar dados do defensor.');
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, [state.cpf, authVisao, location.pathname, navigate, podeSuporteAdministrativoRelatorios]);

  const handleFilterChange = async (filters: any) => {
    try {
      if (!defensorData) return;

      const relatoriosData = await BuscarRelatoriosDoDefensor(
        defensorData.id,
        filters.defensoriaId,
        filters.mes,
        filters.ano,
        filters.tipo,
        filters.status
      );

      setRelatorios(relatoriosData);
      toast.success('Filtro aplicado com sucesso!');
    } catch (error) {
      console.error('Erro ao filtrar relatórios:', error);
      toast.error('Erro ao aplicar filtro.');
    }
  };

  const handleCreateNew = (options: CreateNewOptions) => {
    // A validação já foi feita no componente de filtros
    // Apenas navegar para criar novo relatório
    const params = new URLSearchParams({
      defensoriaId: String(options.defensoriaId),
      defensoriaName: options.defensoriaName,
      mes: options.mes,
      ano: String(options.ano),
      tipo: options.tipo,
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };


  const handleEdit = (relatorio: RelatorioAtividadeDto) => {
    // Navegar para edição do relatório
    const params = new URLSearchParams({
      id: String(relatorio.id),
      defensoriaId: String(relatorio.defensoriaId),
      defensoriaName: relatorio.defensoriaName || '',
      mes: String(relatorio.mes).padStart(2, '0'),
      ano: String(relatorio.ano),
      tipo: relatorio.tipo,
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };

  const handleConfirmDelete = async () => {
    if (!relatorioParaDeletar || !defensorData) return;

    // Validação adicional: garantir que relatórios enviados não possam ser excluídos
    if (relatorioParaDeletar.status === 'enviado') {
      toast.error('Relatórios enviados não podem ser excluídos.');
      setShowDeleteModal(false);
      setRelatorioParaDeletar(null);
      return;
    }

    try {
      setDeleting(true);
      await DeletarRelatorio(relatorioParaDeletar.id);

      // Remover o relatório da lista
      setRelatorios(prev => prev.filter(r => r.id !== relatorioParaDeletar.id));

      toast.success('Relatório excluído com sucesso!');
      setShowDeleteModal(false);
      setRelatorioParaDeletar(null);
    } catch (error: any) {
      console.error('Erro ao deletar relatório:', error);
      const mensagem = error?.response?.data?.message || 'Erro ao excluir relatório.';
      toast.error(mensagem);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRelatorioParaDeletar(null);
  };

  const handleView = (relatorio: RelatorioAtividadeDto) => {
    // Navegar para visualização do relatório (modo somente leitura)
    const params = new URLSearchParams({
      id: String(relatorio.id),
      defensoriaId: String(relatorio.defensoriaId),
      defensoriaName: relatorio.defensoriaName || '',
      mes: String(relatorio.mes).padStart(2, '0'),
      ano: String(relatorio.ano),
      tipo: relatorio.tipo,
      view: 'true', // Flag para modo visualização
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };

  if (loading) {
    return <Loading />;
  }

  if (modoSuporteAdministrativo) {
    return (
      <Fragment>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
            <RelatoriosUserLabel className="mb-1" />
          </div>
          <Alert variant="info" className="mb-3">
            Use os links da barra acima ou os atalhos abaixo para abrir as áreas de Diretoria Regional ou
            Corregedoria, conforme o que o backend permitir ao seu usuário.
          </Alert>
          <p className="mb-2">
            <Link to={Links.Relatorios_Aprovacao}>Diretoria Regional — aprovação de relatórios</Link>
          </p>
          {permissions.relatoriosCorregedoria && (
            <p className="mb-0">
              <Link to={Links.Relatorios_Corregedoria}>Corregedoria — aval final</Link>
            </p>
          )}
          <hr />
          <p className="mb-0">
            <Link to={Links.Index}>Voltar ao início</Link>
          </p>
        </Container>
      </Fragment>
    );
  }

  if (accessDenied) {
    return (
      <Fragment>
        <Container fluid>
          <RelatoriosUserLabel />
          <Alert variant="warning" className="mt-3">
            <Alert.Heading>Acesso restrito</Alert.Heading>
            <p className="mb-0">
              Apenas defensores, colaboradores lotados na Corregedoria (setor Corregedoria) ou usuários com permissão
              administrativa de suporte (ADMIN_ACTIONS com leitura) podem usar este módulo. Defensores acessam a lista
              de relatórios; Corregedoria e suporte administrativo usam as áreas correspondentes na navegação do módulo.
            </p>
            <hr />
            <p className="mb-0">
              <Link to={Links.Index}>Voltar ao início</Link>
            </p>
          </Alert>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Container fluid>
        <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
          <RelatoriosUserLabel className="mb-1" />
        </div>

        <RelatoriosAtividadesNotificacoes
          relatorios={relatorios}
          loading={loading}
          modo="defensor"
        />

        <RelatoriosAtividadesHeader
          defensor={defensorData || undefined}
          comarca={comarca}
          vara={vara}
          titularidade={titularidade}
          lotacaoTitular={lotacaoTitular}
        />

        <RelatoriosAtividadesFilter
          defensoriasOpcoes={defensoriasOpcoes}
          defensorId={defensorData?.id}
          onFilterChange={handleFilterChange}
          onCreateNew={handleCreateNew}
          onPeriodoChange={handlePeriodoChange}
          onViewExisting={handleView}
          onEditExisting={(relatorioId, options) => {
            // Navegar para edição do relatório existente
            const params = new URLSearchParams({
              id: String(relatorioId),
              defensoriaId: String(options.defensoriaId),
              defensoriaName: options.defensoriaName,
              mes: options.mes,
              ano: String(options.ano),
              tipo: options.tipo,
            });
            navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
          }}
        />

        <RelatoriosAtividadesList
          relatorios={relatorios}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          // Defensor não vê o botão de histórico; reservado para telas/fluxos da Corregedoria
          mostrarBotaoHistorico={false}
        />
      </Container>

      {/* Modal de confirmação para excluir relatório */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning" role="alert">
            <strong>Atenção!</strong> Esta ação é permanente e não pode ser desfeita.
          </div>
          {relatorioParaDeletar && (
            <div className="mb-3">
              <p className="mb-2">
                Você está prestes a excluir o seguinte relatório:
              </p>
              <p className="mb-1">
                <strong>Período:</strong> {relatorioParaDeletar.periodo}
              </p>
              <p className="mb-1">
                <strong>Defensoria/Vara:</strong> {relatorioParaDeletar.defensoriaName}
              </p>
              <p className="mb-1">
                <strong>Tipo:</strong> {relatorioParaDeletar.tipo === 'civil' ? 'Cível' : 'Criminal'}
              </p>
              <p className="mb-1">
                <strong>Status:</strong> {
                  relatorioParaDeletar.status === 'rascunho' ? 'Rascunho' :
                    relatorioParaDeletar.status === 'pendente' ? 'Pendente' :
                      relatorioParaDeletar.status === 'enviado' ? 'Enviado' :
                        relatorioParaDeletar.status
                }
              </p>
            </div>
          )}
          <p className="mb-0 text-danger">
            <strong>Tem certeza que deseja excluir este relatório?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleCancelDelete} disabled={deleting}>
            Cancelar
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Excluindo...' : 'Sim, excluir permanentemente'}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
